export const COMMANDS = [
    { name: 'as', aliases: [], usage: '/as <序號|名稱>', summary: '換身分' },
    { name: 'acct', aliases: ['account'], usage: '/acct <帳號名>', summary: '用帳號池進房' },
    { name: 'pm', aliases: [], usage: '/pm <暱稱> <內容>', summary: '私訊' },
    { name: 'save', aliases: [], usage: '/save <內容>', summary: '存成片語' },
    { name: 'unsave', aliases: [], usage: '/unsave <序號|內容>', summary: '刪片語' },
    { name: 'help', aliases: [], usage: '/help', summary: '指令說明' },
];
export const HELP_TEXT = `指令：${COMMANDS.map((spec) => `${spec.usage} ${spec.summary}`).join(' · ')} · /<字> 找片語`;
export const COMMAND_KEYS = `/ 片語 · ${COMMANDS.map((spec) => `/${spec.name}`).join(' ')}`;
export function readSlash(draft) {
    const text = draft.trim();
    if (text === '')
        return null;
    if (!text.startsWith('/'))
        return { kind: 'plain', text };
    const body = text.slice(1);
    const head = body.split(/\s+/, 1)[0] ?? '';
    const lowered = head.toLowerCase();
    const spec = COMMANDS.find((item) => item.name === lowered || item.aliases.includes(lowered));
    if (spec)
        return { kind: 'command', name: spec.name, args: body.slice(head.length).trim() };
    return { kind: 'phrase', query: body.trim() };
}
const NONE = { kind: 'none' };
const MAX_OPTIONS = 8;
function project(hint, enter, options = []) {
    return { hint, options: options.slice(0, MAX_OPTIONS), enter };
}
function splitFirst(args) {
    const head = args.split(/\s+/, 1)[0] ?? '';
    return [head, args.slice(head.length).trim()];
}
function oneBasedIndex(ref, length) {
    if (!/^\d+$/.test(ref))
        return null;
    const index = Number(ref) - 1;
    return index >= 0 && index < length ? index : null;
}
function settle(found) {
    const unique = found.filter((item, index) => found.findIndex((other) => other.id === item.id) === index);
    if (unique.length === 0)
        return null;
    const [only] = unique;
    return unique.length === 1 && only ? { kind: 'one', identity: only } : { kind: 'many', candidates: unique };
}
export function matchIdentity(ref, identities) {
    const key = ref.trim();
    if (key === '')
        return { kind: 'none' };
    const index = oneBasedIndex(key, identities.length);
    const byIndex = index === null ? undefined : identities[index];
    if (byIndex)
        return { kind: 'one', identity: byIndex };
    const depth = Math.max(0, ...identities.map((item) => item.keys.length));
    const tiers = [
        (item) => item.label === key,
        ...Array.from({ length: depth }, (_, tier) => (item) => item.keys[tier] === key),
    ];
    for (const tier of tiers) {
        const settled = settle(identities.filter(tier));
        if (settled)
            return settled;
    }
    const lowered = key.toLowerCase();
    const prefixed = identities.filter((item) => [item.label, ...item.keys].some((name) => name.toLowerCase().startsWith(lowered)));
    return settle(prefixed) ?? { kind: 'none' };
}
function identityListing(identities) {
    return identities.length > 0
        ? identities.map((item, index) => `${index + 1}.${item.label}`).join('  ')
        : '（沒有可切換的身分）';
}
function identityOptions(identities, all) {
    return identities.map((item) => ({
        key: `as:${item.id}`,
        label: `${all.indexOf(item) + 1}. ${item.label}`,
        pick: { kind: 'switch', id: item.id },
    }));
}
function phraseOptions(phrases, matches, pick) {
    return matches.map((text) => ({
        key: `phrase:${text}`,
        label: `${phrases.indexOf(text) + 1}. ${text}`,
        pick: pick(text),
    }));
}
function findPhrases(query, phrases) {
    if (query === '')
        return [...phrases];
    const index = oneBasedIndex(query, phrases.length);
    const byIndex = index === null ? undefined : phrases[index];
    if (byIndex !== undefined)
        return [byIndex];
    const lowered = query.toLowerCase();
    return phrases.filter((text) => text.toLowerCase().includes(lowered));
}
const stage = (text) => ({ kind: 'stage', text });
const unsave = (text) => ({ kind: 'unsave', text });
function projectPhrase(query, phrases) {
    const matches = findPhrases(query, phrases);
    const options = phraseOptions(phrases, matches, stage);
    const [first] = matches;
    if (first !== undefined)
        return project(`Enter 帶入「${first}」，再按一次 Enter 送出 · /help 看指令`, stage(first), options);
    if (phrases.length === 0)
        return project('還沒有片語；/save <內容> 新增一句 · /help 看指令', NONE);
    return project(`沒有片語含「${query}」；/save <內容> 新增一句 · /help 看指令`, NONE);
}
function projectCommand(name, args, ctx) {
    switch (name) {
        case 'as': {
            if (args === '') {
                return project(`用法：/as <序號|名稱>；目前：${identityListing(ctx.identities)}`, NONE, identityOptions(ctx.identities, ctx.identities));
            }
            const match = matchIdentity(args, ctx.identities);
            switch (match.kind) {
                case 'one':
                    return project(`Enter 換成「${match.identity.label}」`, { kind: 'switch', id: match.identity.id });
                case 'many':
                    return project(`「${args}」對到 ${match.candidates.length} 個身分，改用序號或完整名稱`, NONE, identityOptions(match.candidates, ctx.identities));
                case 'none':
                    return project(`找不到「${args}」；目前：${identityListing(ctx.identities)}`, NONE);
                default: {
                    const unreachable = match;
                    return unreachable;
                }
            }
        }
        case 'acct': {
            if (ctx.poolAccounts === null)
                return project('這個控制台沒接帳號池；/help 看可用指令', NONE);
            const [username] = splitFirst(args);
            if (username === '') {
                const pool = ctx.poolAccounts;
                return project(pool.length > 0
                    ? `用法：/acct <帳號名>；池內：${pool.slice(0, MAX_OPTIONS).join('  ')}${pool.length > MAX_OPTIONS ? ` …共 ${pool.length} 顆` : ''}`
                    : '用法：/acct <帳號名>；帳號池是空的（先到右側「帳號」分頁註冊）', NONE);
            }
            return project(`Enter 用帳號池的 ${username} 進房`, { kind: 'acct', username });
        }
        case 'pm': {
            const [target, text] = splitFirst(args);
            if (target === '' || text === '')
                return project('用法：/pm <暱稱> <內容>，例如 /pm 阿明 你好', NONE);
            return project(`Enter 私訊 ${target}`, { kind: 'pm', target, text });
        }
        case 'save': {
            if (args === '')
                return project('用法：/save <內容>，例如 /save 大家好', NONE);
            if (args.startsWith('/'))
                return project('片語不能用 / 開頭', NONE);
            if (ctx.phrases.includes(args))
                return project(`已經有「${args}」這句`, NONE);
            return project(`Enter 存成片語「${args}」`, { kind: 'save', text: args });
        }
        case 'unsave': {
            const index = oneBasedIndex(args, ctx.phrases.length);
            const exact = index === null ? (ctx.phrases.includes(args) ? args : undefined) : ctx.phrases[index];
            if (exact !== undefined)
                return project(`Enter 刪掉片語「${exact}」`, unsave(exact));
            const options = phraseOptions(ctx.phrases, findPhrases(args, ctx.phrases), unsave);
            return project(args === '' ? '用法：/unsave <序號|內容>' : `沒有「${args}」這句；用序號或完整內容`, NONE, options);
        }
        case 'help':
            return project(HELP_TEXT, NONE);
        default: {
            const unreachable = name;
            return unreachable;
        }
    }
}
export function projectDraft(draft, ctx) {
    const slash = readSlash(draft);
    if (slash === null)
        return project(null, NONE);
    switch (slash.kind) {
        case 'plain':
            return project(null, { kind: 'say', text: slash.text });
        case 'phrase':
            return projectPhrase(slash.query, ctx.phrases);
        case 'command':
            return projectCommand(slash.name, slash.args, ctx);
        default: {
            const unreachable = slash;
            return unreachable;
        }
    }
}
//# sourceMappingURL=draft.js.map